/* Studio presentation controls. Variant resolution, fulfilment and the 3D
 * renderer remain owned by the existing pdp.js / pdp-3d.min.js modules. */
(function () {
  'use strict';
  var root = document.querySelector('[data-studio-pdp]');
  if (!root || !window.__pdp) return;
  var text = window.StudioPdpText || {};
  var api = window.__pdp;
  var stage = document.getElementById('pdp-stage');
  var media = document.getElementById('pdp-media');
  var poster = document.getElementById('pdp-poster');
  var primaryThumb = root.querySelector('[data-pdp-thumb-art]');
  var detailPicture = root.querySelector('.spdp-thumb-detail');
  var detailCanvas = root.querySelector('[data-spdp-detail-canvas]');
  var detailReady = false;
  var detailQueued = false;
  var detailKey = '';
  var pause = document.getElementById('spdp-rotation');
  var dialog = document.getElementById('spdp-compare');
  var sticky = document.getElementById('pdp-sticky');
  var atc = document.getElementById('pdp-atc');
  var finishKeys = ['aluminium', 'framed', 'acrylic', 'canvas'];
  var motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var paused = motion.matches;
  var ready = false;
  var fallback = false;
  var zoom = 1;
  var currentSource = '';
  var compareOpener;
  var scrollPending = false;

  function each(selector, fn) { root.querySelectorAll(selector).forEach(fn); }
  function say(selector, value) {
    var node = root.querySelector(selector);
    if (node && node.textContent !== value) node.textContent = value || '';
  }
  function dimensions(state) {
    var size = state.selection && state.selection.size;
    if (!size) return { w: 12, h: 8, circle: false };
    var w = size.w, h = size.h;
    if ((state.orientation === 'portrait' && w > h) || (state.orientation === 'landscape' && w < h)) {
      var swap = w; w = h; h = swap;
    }
    return { w: w, h: h, circle: (size.shape || state.selection.shape) === 'circle' };
  }
  function frameStyle(image, state, thickness) {
    if (!image) return;
    var selection = state.selection || {};
    var framed = state.idx === 1;
    var colours = { black: '#202020', white: '#f5f4ef', oak: '#b49a72' };
    image.style.boxSizing = 'border-box';
    image.style.border = framed ? thickness + 'px solid ' + (colours[String(selection.color).toLowerCase()] || colours.black) : '0';
    image.style.padding = framed && selection.mat ? Math.round(thickness * 1.6) + 'px' : '0';
    image.style.borderRadius = dimensions(state).circle ? '50%' : '0';
  }
  function updateArt() {
    var source = window.CUSTOM_PRINT_MODE ? window.SHOPIFY_PRODUCT_IMAGE : primaryThumb && primaryThumb.getAttribute('src');
    if (!source || source === currentSource) return;
    currentSource = source;
    each('[data-spdp-art]', function (img) { img.src = source; });
    invalidateDetail();
  }
  function invalidateDetail() {
    detailReady = false;
    if (detailPicture) detailPicture.classList.remove('has-model-snapshot');
  }
  function syncDetail(state) {
    if (!detailPicture) return;
    var selection = state.selection || {};
    var dims = dimensions(state);
    var colours = { black: '#202020', white: '#f5f4ef', oak: '#b49a72' };
    detailPicture.style.setProperty('--spdp-detail-frame', colours[String(selection.color).toLowerCase()] || colours.black);
    detailPicture.dataset.mat = String(!!selection.mat);
    detailPicture.dataset.shape = dims.circle ? 'circle' : 'rect';
    var key = [state.idx, state.orientation, dims.w, dims.h, dims.circle, selection.color, selection.mat, currentSource].join('|');
    if (key !== detailKey) {
      detailKey = key;
      invalidateDetail();
    }
  }
  function captureDetailModel(viewer, canvas) {
    if (!viewer || !viewer.product || !viewer.renderer || !viewer.camera || !viewer.scene || !canvas) return false;
    var dims = viewer.dims;
    var source = viewer.renderer.domElement;
    if (!dims || !(dims.width > 0) || !(dims.height > 0) || !source.width || !source.height) return false;
    var context = canvas.getContext('2d');
    if (!context) return false;
    var scene = null;
    var rendered = false;
    var captured = false;
    try {
      scene = new viewer.scene.constructor();
      scene.environment = viewer.scene.environment;
      viewer.scene.children.forEach(function (child) {
        if (!child.isLight || child === viewer.shadowLight) return;
        var light = child.clone();
        if (child === viewer.rearLight) light.visible = true;
        light.castShadow = false;
        scene.add(light);
      });
      var product = viewer.product.clone(true);
      product.visible = true;
      product.traverse(function (node) {
        if (node.isMesh) { node.castShadow = false; node.receiveShadow = false; }
        if ((viewer._glares || []).some(function (glare) { return node.geometry === glare.geometry && node.material === glare.material; })) node.visible = false;
      });
      var camera = viewer.camera.clone();
      camera.fov = viewer._baseFov || 40;
      camera.aspect = source.width / source.height;
      camera.position.set(0, 0, 7);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      var visibleHeight = 14 * Math.tan(camera.fov * Math.PI / 360);
      var scale = Math.min(visibleHeight * 0.72 / dims.height, visibleHeight * camera.aspect * 0.8 / dims.width) * 2.3;
      product.scale.setScalar(scale);
      product.rotation.set(0.1, -0.5, 0);
      product.position.set(dims.width * scale * 0.4, dims.height * scale * -0.4, 0);
      scene.add(product);
      canvas.width = 360;
      canvas.height = Math.max(1, Math.round(360 / camera.aspect));
      rendered = true;
      viewer.renderer.render(scene, camera);
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(source, 0, 0, canvas.width, canvas.height);
      captured = true;
    } catch (error) {
      captured = false;
    } finally {
      if (rendered) {
        try { viewer.renderer.render(viewer.scene, viewer.camera); }
        catch (error) { captured = false; }
      }
      if (scene) scene.clear();
    }
    return captured;
  }
  function queueDetailCapture() {
    if (detailQueued || !detailReady || !detailCanvas || !detailPicture) return;
    detailQueued = true;
    requestAnimationFrame(function () {
      detailQueued = false;
      if (!detailReady) return;
      detailPicture.classList.toggle('has-model-snapshot', captureDetailModel(window.pdpViewer, detailCanvas));
    });
  }
  function renderPoster() {
    if (ready || !poster || !poster.isConnected) return;
    var state = api.state();
    var dims = dimensions(state);
    var ratio = dims.w / dims.h;
    var room = (window.PDP_ROOMS || [])[state.room || 0];
    var inRoom = fallback && state.view === 'room' && room;
    var width = Math.min(stage.clientWidth * 0.78, stage.clientHeight * 0.73 * ratio);
    if (inRoom) width = stage.clientWidth * Math.min(0.38, Math.max(0.1, dims.w / room.wallWidthIn));
    if (fallback && state.view === 'detail') width *= 1.65;
    width *= zoom;
    poster.style.width = width + 'px';
    poster.style.height = width / ratio + 'px';
    poster.style.maxWidth = 'none';
    poster.style.maxHeight = 'none';
    poster.style.left = inRoom ? room.hangU * 100 + '%' : '50%';
    poster.style.top = inRoom ? room.hangV * 100 + '%' : '50%';
    frameStyle(poster, state, Math.max(5, Math.round(width * 0.022)));
    stage.style.backgroundImage = inRoom ? 'url("' + room.url + '")' : '';
    if (fallback) say('[data-spdp-viewer-status]', state.view === 'back' ? text.back_unavailable : text.fallback_hint);
  }
  function syncSelection() {
    var state = api.state();
    var key = finishKeys[state.idx];
    var dims = dimensions(state);
    root.dataset.finish = key;
    root.dataset.thumbOrientation = state.orientation;
    root.style.setProperty('--spdp-ratio', dims.w / dims.h);
    root.style.setProperty('--spdp-room-width', Math.min(36, Math.max(17, dims.w / 1.3)) + '%');
    say('[data-spdp-finish-caption]', text[key + '_note']);
    say('[data-spdp-summary-price]', document.getElementById('pdp-price').textContent);
    each('[data-spdp-choose]', function (button) {
      var nativeButton = root.querySelector('.pdp-finish[data-finish="' + button.dataset.spdpChoose + '"]');
      button.disabled = !nativeButton || nativeButton.disabled;
      var selected = button.dataset.spdpChoose === key;
      button.classList.toggle('is-active', selected);
      button.setAttribute('aria-pressed', String(selected));
      var label = button.querySelector('[data-spdp-choice-state]');
      if (label) label.textContent = button.disabled ? text.unavailable : selected ? text.selected : text.choose;
    });
    frameStyle(root.querySelector('.spdp-selected-art'), state, 4);
    updateArt();
    syncDetail(state);
    renderPoster();
    queueSticky();
  }

  each('[data-spdp-compare-open]', function (button) {
    button.addEventListener('click', function () {
      compareOpener = button;
      if (dialog && !dialog.open) dialog.showModal();
    });
  });
  if (dialog) {
    dialog.querySelector('[data-spdp-compare-close]').addEventListener('click', function () { dialog.close(); });
    dialog.addEventListener('click', function (event) {
      var bounds = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom)) dialog.close();
    });
    dialog.addEventListener('close', function () { if (compareOpener) compareOpener.focus({ preventScroll: true }); });
  }
  each('[data-spdp-choose]', function (button) {
    button.addEventListener('click', function () {
      var choice = root.querySelector('.pdp-finish[data-finish="' + button.dataset.spdpChoose + '"]');
      if (!choice || choice.disabled) return;
      choice.click();
      if (dialog && dialog.open) dialog.close();
      else {
        document.getElementById('spdp-options').scrollIntoView({ behavior: motion.matches ? 'auto' : 'smooth', block: 'start' });
        choice.focus({ preventScroll: true });
      }
    });
  });
  each('[data-spdp-wall-open]', function (button) { button.addEventListener('click', function () { document.getElementById('pdp-viz-open').click(); }); });
  each('[data-spdp-size-open]', function (button) { button.addEventListener('click', function () { document.getElementById('pdp-sizeguide-open').click(); }); });

  function updateSticky() {
    scrollPending = false;
    if (!sticky || !atc) return;
    var bounds = atc.getBoundingClientRect();
    var page = root.getBoundingClientRect();
    var visible = bounds.bottom > 0 && bounds.top < window.innerHeight - 8;
    sticky.classList.toggle('is-visible', !visible && page.top < window.innerHeight && page.bottom > 100 && !media.classList.contains('is-fs'));
  }
  function queueSticky() {
    if (scrollPending) return;
    scrollPending = true;
    requestAnimationFrame(updateSticky);
  }
  window.addEventListener('scroll', queueSticky, { passive: true });
  window.addEventListener('resize', function () { queueSticky(); renderPoster(); queueDetailCapture(); });
  if (typeof ResizeObserver !== 'undefined' && stage) new ResizeObserver(queueDetailCapture).observe(stage);
  new MutationObserver(queueSticky).observe(media, { attributes: true, attributeFilter: ['class'] });

  function applyMotion() {
    if (pause) {
      pause.textContent = paused ? text.play : text.pause;
      pause.setAttribute('aria-pressed', String(paused));
      pause.hidden = !ready || api.state().view !== 'front';
    }
    var viewer = window.pdpViewer;
    if (viewer && paused) { clearTimeout(viewer._idleTimer); viewer.autoRotate = false; }
  }
  if (pause) pause.addEventListener('click', function () {
    paused = !paused;
    applyMotion();
    if (window.pdpViewer && !paused) window.pdpViewer.autoRotate = true;
  });
  motion.addEventListener('change', function (event) { paused = event.matches; applyMotion(); });
  stage.addEventListener('pointerup', function () { if (paused) applyMotion(); });
  stage.addEventListener('pointercancel', function () { if (paused) applyMotion(); });
  stage.addEventListener('keydown', function (event) {
    if (event.target !== stage || !ready || api.state().view === 'room') return;
    var viewer = window.pdpViewer;
    if (!viewer) return;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      paused = true;
      applyMotion();
      viewer.targetRot.y += event.key === 'ArrowLeft' ? -0.25 : 0.25;
    }
  });
  function viewerReady() {
    if (!document.querySelector('#pdp-stage-host canvas')) return;
    ready = true;
    fallback = false;
    root.classList.remove('is-fallback');
    root.classList.add('has-3d');
    stage.style.backgroundImage = '';
    say('[data-spdp-status]', text.interactive);
    say('[data-spdp-viewer-status]', text.viewer_hint);
    applyMotion();
    if (window.pdpConfigured) { detailReady = true; queueDetailCapture(); }
  }
  function viewerFallback() {
    if (ready) return;
    fallback = true;
    root.classList.add('is-fallback');
    say('[data-spdp-status]', text.fallback);
    renderPoster();
  }
  window.addEventListener('pdpviewer:booted', applyMotion);
  window.addEventListener('pdpviewer:ready', viewerReady);
  window.addEventListener('studio:3dconfigured', function () { applyMotion(); updateArt(); syncDetail(api.state()); detailReady = true; queueDetailCapture(); });
  window.addEventListener('studio:selection', syncSelection);
  window.addEventListener('error', function (event) {
    if (/WebGL.*context|context.*WebGL/i.test(event.message || '')) viewerFallback();
  });
  new MutationObserver(function () { zoom = 1; renderPoster(); applyMotion(); }).observe(root, { attributes: true, attributeFilter: ['data-view'] });
  var chips = document.getElementById('pdp-room-chips');
  if (chips) chips.addEventListener('click', renderPoster);
  if (primaryThumb) new MutationObserver(updateArt).observe(primaryThumb, { attributes: true, attributeFilter: ['src'] });
  [['pdp-zoom-in', 0.15], ['pdp-zoom-out', -0.15]].forEach(function (entry) {
    document.getElementById(entry[0]).addEventListener('click', function () {
      if (!fallback) return;
      zoom = Math.max(0.5, Math.min(2.3, zoom + entry[1]));
      renderPoster();
    });
  });
  document.getElementById('pdp-reset').addEventListener('click', function () { zoom = 1; renderPoster(); applyMotion(); });
  document.getElementById('pdp-expand').addEventListener('click', renderPoster);
  document.getElementById('pdp-fs-exit').addEventListener('click', renderPoster);
  window.addEventListener('studio:fullscreen', function () { requestAnimationFrame(function () { renderPoster(); queueSticky(); }); });
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && media.classList.contains('is-fs')) document.getElementById('pdp-fs-exit').click();
  });
  syncSelection();
  applyMotion();
  if (window.pdpViewer && window.pdpConfigured) viewerReady();
  if (window.PDP_BOOT) window.PDP_BOOT.request();
  setTimeout(function () { if (!ready) viewerFallback(); }, 8000);
})();
